import { formatUnits, parseUnits } from 'viem'

import { SwapQuoteError } from '~/components/forms/swap/hooks/swapQuoteError'

// Ceil to the display precision so a rounded shortfall is never understated.
const formatShortfall = ({
  amount,
  decimals,
}: {
  amount: bigint
  decimals: number
}) => {
  const displayDecimals = Math.min(decimals, 6)
  const factor = 10n ** BigInt(decimals - displayDecimals)
  return formatUnits((amount + factor - 1n) / factor, displayDecimals)
}

/**
 * Maps a failed payment-source quote to its inline error message: the exact
 * input-token shortfall when the server reported structured
 * insufficient-balance amounts, otherwise generic failure copy.
 */
export const getPaymentSourceQuoteError = ({
  quoteFailed,
  failureReason,
  sellToken,
}: {
  quoteFailed: boolean
  failureReason: unknown
  sellToken: { symbol: string; decimals: number } | undefined
}): string | undefined => {
  if (!quoteFailed) return undefined

  const insufficientBalance =
    failureReason instanceof SwapQuoteError
      ? failureReason.insufficientBalance
      : undefined

  if (!insufficientBalance || !sellToken)
    return "Couldn't fetch a quote for this payment source. Try another token."

  // The server reports decimal token amounts; convert back to base units so
  // the shortfall subtraction stays exact.
  const shortfall =
    parseUnits(insufficientBalance.requiredAmount, sellToken.decimals) -
    parseUnits(insufficientBalance.availableBalance, sellToken.decimals)

  if (shortfall <= 0n)
    return "Couldn't fetch a quote for this payment source. Try another token."

  const formattedShortfall = formatShortfall({
    amount: shortfall,
    decimals: sellToken.decimals,
  })
  return `You need ${formattedShortfall} more ${sellToken.symbol} to cover fees for this transfer.`
}
